import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { SONIOX_VOICES } from "../lib/schemaModes";
import { createChairEngine } from "../lib/chairAudioEngine";
import { postChair, postRescript } from "../api";
import { useToast } from "../../../components/Toast";
import { PlaybackControls } from "./PlaybackControls";

interface PendulationToolProps {
  patientId: string;
  sessionId?: string;
  language: "en" | "hu";
  getToken: () => Promise<string | null>;
}

/* Somatic pendulation: alternate between the resource and the stressor —
   the two scenes rendered, a paced narration underneath, and a toggle the
   therapist flips as the client swings between them. */
export function PendulationTool({ patientId, sessionId, language, getToken }: PendulationToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [stressor, setStressor] = useState("");
  const [resource, setResource] = useState("");
  const [narration, setNarration] = useState("");
  const [voice, setVoice] = useState("Grace");
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState<{ stressor: string; resource: string } | null>(null);
  const [showingResource, setShowingResource] = useState(true);
  const [generated, setGenerated] = useState(false);
  const [looping, setLooping] = useState(false);

  const engineRef = useRef<ReturnType<typeof createChairEngine> | null>(null);

  const onGenerate = async () => {
    try {
      setBusy(true);
      await engineRef.current?.close();
      const [visual, chair] = await Promise.all([
        // scene = the stressor, rescript = the resource: same pair endpoint
        postRescript(getToken, {
          patient_id: patientId, session_id: sessionId,
          scene_prompt: stressor, rescript_prompt: resource,
        }),
        postChair(getToken, {
          patient_id: patientId, session_id: sessionId, language,
          chairs: [{ id: crypto.randomUUID(), mode: "pendulation", label: "", text: narration, voice }],
        }),
      ]);
      const engine = createChairEngine(chair.clips);
      await engine.load((url) => fetch(url).then((r) => r.arrayBuffer()));
      engineRef.current = engine;
      setImages({ stressor: visual.before_url, resource: visual.after_url });
      setShowingResource(true);
      setGenerated(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg.includes("image_policy_declined") ? t("experiential.policyDeclined") : `${t("experiential.genError")}: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    return () => {
      engineRef.current?.close();
    };
  }, []);

  return (
    <div>
      <TextField
        label={t("experiential.pendulationStressor")}
        multiline
        minRows={2}
        fullWidth
        size="small"
        value={stressor}
        onChange={(e) => setStressor(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        label={t("experiential.pendulationResource")}
        multiline
        minRows={2}
        fullWidth
        size="small"
        value={resource}
        onChange={(e) => setResource(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        label={t("experiential.pendulationNarration")}
        multiline
        minRows={2}
        fullWidth
        size="small"
        value={narration}
        onChange={(e) => setNarration(e.target.value)}
        sx={{ mb: 2 }}
      />
      <FormControl size="small" sx={{ minWidth: 130, mb: 2 }}>
        <InputLabel>{t("experiential.readBy")}</InputLabel>
        <Select value={voice} label={t("experiential.readBy")} onChange={(e) => setVoice(e.target.value)}>
          {SONIOX_VOICES.map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="exp-chair-controls">
        <Button
          variant="contained"
          size="small"
          onClick={onGenerate}
          disabled={busy || !stressor.trim() || !resource.trim() || !narration.trim()}
        >
          {t("experiential.generate")}
        </Button>
        {generated && (
          <>
            <PlaybackControls
              onPlay={() => engineRef.current?.play()}
              onStop={() => engineRef.current?.stop()}
              looping={looping}
              onToggleLoop={() => {
                const next = !looping;
                setLooping(next);
                engineRef.current?.setLoop(next);
              }}
            />
            <Button size="small" variant="outlined" onClick={() => setShowingResource((v) => !v)}>
              {showingResource ? t("experiential.pendulateToStressor") : t("experiential.pendulateToResource")}
            </Button>
          </>
        )}
      </div>

      {images && (
        <div className="exp-pendulum-stage">
          <img
            src={images.resource}
            alt={t("experiential.pendulationResource")}
            className={showingResource ? "is-active" : ""}
          />
          <img
            src={images.stressor}
            alt={t("experiential.pendulationStressor")}
            className={!showingResource ? "is-active" : ""}
          />
        </div>
      )}
    </div>
  );
}
